import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { logError } from '../../../utils/logger';
import type { QuotationPdfFile } from '../../admin/admin.api';
import QuotationPdfViewer from '../../work-implementation/reports/QuotationPdfViewer';
import { BillDetail, billingApi, BillPayment, fetchBillPdf, fetchReceiptPdf, PAYMENT_MODES, rupees } from '../billing.api';
import { BillStatusChip } from './BillStatusChip';
import ConvertProformasDialog from './ConvertProformasDialog';

type Panel = 'payment' | 'discount' | 'cancel' | null;

/**
 * One proforma or bill: its lines and receipts, and what can be done with it — view the PDF,
 * convert a proforma into a bill, record a receipt (an advance, on a proforma, with its printed
 * receipt), give a discount, cancel.
 */
export default function BillDetailDialog({
  billId,
  onClose,
  onChanged,
  onOpenBill,
}: {
  billId: number | null;
  onClose: () => void;
  onChanged: () => void;
  onOpenBill: (id: number) => void;
}) {
  const [detail, setDetail] = useState<BillDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [panel, setPanel] = useState<Panel>(null);
  const [pdf, setPdf] = useState<QuotationPdfFile | null>(null);
  const [pdfTitle, setPdfTitle] = useState('');
  const pdfUrl = useRef<string | null>(null);

  const [payDate, setPayDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [payAmount, setPayAmount] = useState('');
  const [payTds, setPayTds] = useState('');
  const [payMode, setPayMode] = useState('NEFT');
  const [payRef, setPayRef] = useState('');
  const [discount, setDiscount] = useState('');
  const [discountNote, setDiscountNote] = useState('');
  const [reason, setReason] = useState('');
  const [converting, setConverting] = useState(false);

  const load = useCallback(async () => {
    if (billId == null) return;
    setLoading(true);
    try {
      const d = await billingApi.get(billId);
      setDetail(d);
      setDiscount(String(Number(d.bill.discountAmount) || ''));
      setDiscountNote(d.discountNote ?? '');
    } catch (e) {
      toast.error(logError({ message: 'Could not load the bill', error: e, code: 'BILL_LOAD_FAILED' }));
    } finally {
      setLoading(false);
    }
  }, [billId]);

  useEffect(() => {
    setDetail(null);
    setPanel(null);
    load();
  }, [load]);

  useEffect(() => () => {
    if (pdfUrl.current) URL.revokeObjectURL(pdfUrl.current);
  }, []);

  const run = async (fn: () => Promise<BillDetail>, ok: string) => {
    setBusy(true);
    try {
      const d = await fn();
      toast.success(ok);
      setPanel(null);
      onChanged();
      if (d.bill.id !== billId) onOpenBill(d.bill.id);
      else setDetail(d);
    } catch (e) {
      toast.error(logError({ message: 'That did not go through', error: e, code: 'BILL_ACTION_FAILED' }));
    } finally {
      setBusy(false);
    }
  };

  const showPdf = async (fetch: () => Promise<QuotationPdfFile>, title: string) => {
    setBusy(true);
    try {
      const file = await fetch();
      if (pdfUrl.current) URL.revokeObjectURL(pdfUrl.current);
      pdfUrl.current = file.url;
      setPdfTitle(title);
      setPdf(file);
    } catch (e) {
      toast.error(logError({ message: 'Could not open the PDF', error: e, code: 'BILL_PDF_FAILED' }));
    } finally {
      setBusy(false);
    }
  };

  const viewPdf = () => {
    if (!detail) return;
    const d = detail.bill;
    showPdf(() => fetchBillPdf(d.id, d.billNo), `${d.billType === 'INVOICE' ? 'Bill' : 'Proforma'} ${d.billNo}`);
  };
  const viewReceipt = (p: BillPayment) => {
    if (!detail || !p.receiptNo) return;
    const id = detail.bill.id;
    showPdf(() => fetchReceiptPdf(id, p.id, p.receiptNo!), `Advance receipt ${p.receiptNo}`);
  };

  const b = detail?.bill;
  const isBill = b?.billType === 'INVOICE';
  /** On an open proforma: what is still to come in as advance. */
  const advanceLeft = b && !isBill ? Math.max(0, Number(b.totalAmount) - Number(b.received) - Number(b.tds)) : 0;
  const live = b?.status === 'ACTIVE';

  return (
    <>
      <Dialog open={billId != null} onClose={busy ? undefined : onClose} fullWidth maxWidth='md'>
        <DialogTitle>
          <Stack direction='row' alignItems='center' gap={1.5} flexWrap='wrap'>
            {b ? `${isBill ? 'Bill' : 'Proforma'} ${b.billNo}` : 'Bill'}
            {b && <BillStatusChip bill={b} />}
          </Stack>
        </DialogTitle>
        <DialogContent>
          {loading && <LinearProgress />}
          {detail && b && (
            <Stack gap={2}>
              {b.status === 'CANCELLED' && (
                <Alert severity='error'>Cancelled {dayjs(detail.cancelledAt).format('DD MMM YYYY')}: {detail.cancelReason}</Alert>
              )}
              {b.convertedBillNo && (
                <Alert severity='info'
                  action={<Button color='inherit' size='small' onClick={() => onOpenBill(b.convertedBillId!)}>Open bill</Button>}>
                  Converted into bill {b.convertedBillNo}.
                </Alert>
              )}
              {b.proformaNo && (
                <Alert severity='info' icon={false}
                  action={<Button color='inherit' size='small' onClick={() => onOpenBill(b.proformaId!)}>Open proforma</Button>}>
                  Raised from {b.proformaNo.includes(',') ? 'proformas' : 'proforma'} {b.proformaNo}.
                </Alert>
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} gap={3}>
                <Stack gap={0.5} flex={1}>
                  <Typography variant='caption' color='text.secondary'>Client</Typography>
                  <Typography fontWeight={600}>{b.clientName ?? '—'}</Typography>
                  <Typography variant='body2'>{detail.clientAddress}</Typography>
                  <Typography variant='body2'>{detail.clientPhone}{detail.clientGstNo ? ` · GST ${detail.clientGstNo}` : ''}</Typography>
                </Stack>
                <Stack gap={0.5} flex={1}>
                  <Typography variant='caption' color='text.secondary'>Job</Typography>
                  <Typography fontWeight={600}>{b.jobCode} · {b.jobName}</Typography>
                  <Typography variant='body2'>{b.companyName} · {dayjs(b.billDate).format('DD MMM YYYY')} · FY {b.financialYear}</Typography>
                  {detail.workOrderNo && <Typography variant='body2'>Work order {detail.workOrderNo}</Typography>}
                </Stack>
              </Stack>

              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Work description</TableCell>
                    <TableCell align='right'>Amount</TableCell>
                    <TableCell align='right'>SGST</TableCell>
                    <TableCell align='right'>CGST</TableCell>
                    <TableCell align='right'>With GST</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detail.items.map((i, n) => (
                    <TableRow key={i.id}>
                      <TableCell>{n + 1}</TableCell>
                      <TableCell>{i.description}</TableCell>
                      <TableCell align='right'>{rupees(i.amount)}</TableCell>
                      <TableCell align='right'>{rupees(i.sgstAmount)}</TableCell>
                      <TableCell align='right'>{rupees(i.cgstAmount)}</TableCell>
                      <TableCell align='right'>{rupees(i.totalAmount)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell {...({ colSpan: 2 } as object)}><b>Total</b></TableCell>
                    <TableCell align='right'><b>{rupees(b.amount)}</b></TableCell>
                    <TableCell align='right'><b>{rupees(detail.sgstAmount)}</b></TableCell>
                    <TableCell align='right'><b>{rupees(detail.cgstAmount)}</b></TableCell>
                    <TableCell align='right'><b>{rupees(b.totalAmount)}</b></TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              {(isBill || detail.payments.length > 0) && (
                <>
                  {isBill ? (
                    <Stack direction='row' gap={3} flexWrap='wrap'>
                      <Figure label='Billed' value={rupees(b.totalAmount)} />
                      <Figure label='Received' value={rupees(b.received)} />
                      <Figure label='TDS' value={rupees(b.tds)} />
                      <Figure label='Discount' value={rupees(b.discountAmount)} note={detail.discountNote} />
                      <Figure label='Due' value={rupees(b.due)} strong />
                    </Stack>
                  ) : (
                    <Stack direction='row' gap={3} flexWrap='wrap'>
                      <Figure label='Proforma total' value={rupees(b.totalAmount)} />
                      <Figure label='Advance received' value={rupees(b.received)} />
                      <Figure label='TDS' value={rupees(b.tds)} />
                      <Figure label='Still to receive' value={rupees(advanceLeft)} />
                    </Stack>
                  )}
                  {!isBill && live && (
                    <Typography variant='body2' color='text.secondary'>
                      Advances move to the tax invoice as receipts when this proforma is converted.
                    </Typography>
                  )}
                  {detail.payments.length > 0 && (
                    <Table size='small'>
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Receipt</TableCell>
                          <TableCell align='right'>Received</TableCell>
                          <TableCell align='right'>TDS</TableCell>
                          <TableCell>Mode</TableCell>
                          <TableCell>Reference</TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detail.payments.map((p) => (
                          <TableRow key={p.id}>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{dayjs(p.paymentDate).format('DD MMM YYYY')}</TableCell>
                            <TableCell>
                              {p.receiptNo ? (
                                <Stack>
                                  <Button size='small' sx={{ p: 0, minWidth: 0, justifyContent: 'flex-start', textTransform: 'none' }}
                                    startIcon={<Icon icon='mdi:file-pdf-box' />} disabled={busy} onClick={() => viewReceipt(p)}>
                                    {p.receiptNo}
                                  </Button>
                                  {isBill && p.proformaNo && (
                                    <Typography variant='caption' color='text.secondary'>Advance on {p.proformaNo}</Typography>
                                  )}
                                </Stack>
                              ) : '—'}
                            </TableCell>
                            <TableCell align='right'>{rupees(p.amount)}</TableCell>
                            <TableCell align='right'>{rupees(p.tdsAmount)}</TableCell>
                            <TableCell>{p.paymentMode ?? '—'}</TableCell>
                            <TableCell>{p.referenceNo ?? '—'}{p.notes ? ` · ${p.notes}` : ''}</TableCell>
                            <TableCell align='right'>
                              {live && (
                                <IconButton size='small' aria-label='Delete receipt' disabled={busy}
                                  onClick={() => run(() => billingApi.deletePayment(b.id, p.id), 'Receipt deleted')}>
                                  <Icon icon='mdi:delete-outline' />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </>
              )}

              {panel === 'payment' && (
                <Stack gap={1.5}>
                  <Divider />
                  <Typography fontWeight={600}>
                    {isBill ? `Record receipt · due ${rupees(b.due)}` : `Record advance · still to receive ${rupees(advanceLeft)}`}
                  </Typography>
                  {!isBill && (
                    <Typography variant='body2' color='text.secondary'>
                      The advance gets a receipt number and a printed receipt. It moves to the tax invoice when this proforma is converted.
                    </Typography>
                  )}
                  <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                    <TextField label='Date' type='date' size='small' value={payDate} onChange={(e) => setPayDate(e.target.value)}
                      InputLabelProps={{ shrink: true }} inputProps={{ max: dayjs().format('YYYY-MM-DD') }} />
                    <TextField label='Amount received' type='number' size='small' value={payAmount} onChange={(e) => setPayAmount(e.target.value)} />
                    <TextField label='TDS deducted' type='number' size='small' value={payTds} onChange={(e) => setPayTds(e.target.value)} />
                    <TextField select label='Mode' size='small' value={payMode} onChange={(e) => setPayMode(e.target.value)} sx={{ minWidth: 110 }}>
                      {PAYMENT_MODES.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                    </TextField>
                    <TextField label='UTR / Cheque no.' size='small' value={payRef} onChange={(e) => setPayRef(e.target.value)} />
                  </Stack>
                  <Stack direction='row' gap={1} justifyContent='flex-end'>
                    <Button onClick={() => setPanel(null)}>Close</Button>
                    <Button variant='contained' disabled={busy}
                      onClick={() => run(() => billingApi.addPayment(b.id, {
                        paymentDate: payDate, amount: Number(payAmount || 0), tdsAmount: Number(payTds || 0),
                        paymentMode: payMode, referenceNo: payRef || undefined,
                      }), isBill ? 'Receipt recorded' : 'Advance recorded')}>
                      {isBill ? 'Save receipt' : 'Save advance'}
                    </Button>
                  </Stack>
                </Stack>
              )}
              {panel === 'discount' && (
                <Stack gap={1.5}>
                  <Divider />
                  <Typography fontWeight={600}>Discount / write-off</Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                    <TextField label='Discount amount' type='number' size='small' value={discount} onChange={(e) => setDiscount(e.target.value)} />
                    <TextField label='Note' size='small' fullWidth value={discountNote} onChange={(e) => setDiscountNote(e.target.value)} />
                  </Stack>
                  <Stack direction='row' gap={1} justifyContent='flex-end'>
                    <Button onClick={() => setPanel(null)}>Close</Button>
                    <Button variant='contained' disabled={busy}
                      onClick={() => run(() => billingApi.discount(b.id, Number(discount || 0), discountNote || undefined), 'Discount saved')}>
                      Save discount
                    </Button>
                  </Stack>
                </Stack>
              )}
              {panel === 'cancel' && (
                <Stack gap={1.5}>
                  <Divider />
                  <Typography fontWeight={600} color='error'>Cancel {b.billNo}</Typography>
                  <TextField label='Reason' size='small' required value={reason} onChange={(e) => setReason(e.target.value)} />
                  <Stack direction='row' gap={1} justifyContent='flex-end'>
                    <Button onClick={() => setPanel(null)}>Keep it</Button>
                    <Button variant='contained' color='error' disabled={busy || !reason.trim()}
                      onClick={() => run(() => billingApi.cancel(b.id, reason.trim()), `${b.billNo} cancelled`)}>
                      Cancel {isBill ? 'bill' : 'proforma'}
                    </Button>
                  </Stack>
                </Stack>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          {b && (
            <>
              <Button startIcon={<Icon icon='mdi:file-pdf-box' />} onClick={viewPdf} disabled={busy}>View PDF</Button>
              {live && !isBill && (
                <Button variant='outlined' disabled={busy} onClick={() => setConverting(true)}>
                  Convert to tax invoice…
                </Button>
              )}
              {live && !isBill && advanceLeft > 0 && (
                <Button variant='outlined' onClick={() => { setPayAmount(String(advanceLeft)); setPayTds(''); setPayRef(''); setPanel('payment'); }}>
                  Record advance
                </Button>
              )}
              {live && isBill && Number(b.due) > 0 && (
                <Button variant='outlined' onClick={() => { setPayAmount(String(b.due)); setPayTds(''); setPayRef(''); setPanel('payment'); }}>
                  Record receipt
                </Button>
              )}
              {live && isBill && <Button onClick={() => setPanel('discount')}>Discount</Button>}
              {live && <Button color='error' onClick={() => { setReason(''); setPanel('cancel'); }}>Cancel…</Button>}
            </>
          )}
          <Button onClick={onClose} sx={{ ml: 'auto' }}>Close</Button>
        </DialogActions>
      </Dialog>
      <ConvertProformasDialog
        proformaIds={converting && b ? [b.id] : []}
        onClose={() => setConverting(false)}
        onConverted={(d) => { setConverting(false); onChanged(); onOpenBill(d.bill.id); }}
      />
      <QuotationPdfViewer file={pdf} title={pdfTitle}
        onClose={() => { if (pdfUrl.current) URL.revokeObjectURL(pdfUrl.current); pdfUrl.current = null; setPdf(null); }} />
    </>
  );
}

function Figure({ label, value, note, strong }: { label: string; value: string; note?: string | null; strong?: boolean }) {
  return (
    <Stack>
      <Typography variant='caption' color='text.secondary'>{label}</Typography>
      <Typography fontWeight={strong ? 700 : 500} color={strong ? 'error.main' : undefined}>{value}</Typography>
      {note && <Typography variant='caption' color='text.secondary'>{note}</Typography>}
    </Stack>
  );
}

